/**
 * excludeList.js
 *
 * Persisted host-pattern exclude list — mirrors Proxyman's SSL Proxying list behaviour.
 *
 * Pattern matching rules (same as Proxyman):
 *   - Matched against the HOSTNAME only (no port, no path, no query)
 *   - * matches any sequence of characters  e.g.  *.doubleclick.net
 *   - ? matches any single character        e.g.  api?.example.com
 *   - Exact match when no wildcards         e.g.  firestore.googleapis.com
 *   - Case-insensitive
 *
 * Storage key: "adlens_exclude_list"
 * Format: string[] of patterns
 */

const STORAGE_KEY = 'adlens_exclude_list';

// Empty by default — nothing is filtered until the user adds patterns.
const DEFAULTS = [];

const OLD_FIREBASE_DEFAULTS = ['firestore.googleapis.com', 'firebaselogging-pa.googleapis.com'];

export function loadExcludeList() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Migration: clear the old auto-seeded Firebase defaults that caused traffic
      // to silently disappear for users who never customised the list.
      const isOldDefault = Array.isArray(parsed)
        && parsed.length === 2
        && OLD_FIREBASE_DEFAULTS.every(p => parsed.includes(p));
      if (isOldDefault) {
        saveExcludeList(DEFAULTS);
        return [...DEFAULTS];
      }
      return parsed;
    }
  } catch { /* ignore */ }
  saveExcludeList(DEFAULTS);
  return [...DEFAULTS];
}

export function saveExcludeList(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch { /* ignore */ }
}

export function addExcludePattern(pattern) {
  const list = loadExcludeList();
  const trimmed = pattern.trim().toLowerCase();
  if (!trimmed || list.includes(trimmed)) return list;
  const next = [...list, trimmed];
  saveExcludeList(next);
  return next;
}

export function removeExcludePattern(pattern) {
  const list = loadExcludeList();
  const next = list.filter(p => p !== pattern);
  saveExcludeList(next);
  return next;
}

/**
 * Convert a Proxyman-style glob pattern to a RegExp.
 *   *  →  .*   (any sequence)
 *   ?  →  .    (any single char)
 * All other regex metacharacters are escaped.
 */
function patternToRegex(pattern) {
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  const regexStr = escaped.replace(/\*/g, '.*').replace(/\?/g, '.');
  return new RegExp('^' + regexStr + '$', 'i');
}

/**
 * Extract `hostname + pathname` from a URL for matching.
 * Port is stripped; query/hash are ignored.
 * e.g. "https://api.foxnews.com/v3/alerts?x=1" → "api.foxnews.com/v3/alerts"
 */
export function hostnameFromUrl(url) {
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, ''); // strip trailing slash
    return (u.hostname + path) || '';
  } catch {
    return '';
  }
}

/**
 * Returns true if the given host+path string matches any pattern in the list.
 *
 * Pattern rules:
 *   - Patterns WITHOUT a slash are matched against the hostname only
 *     (e.g.  "api.foxnews.com"  blocks all paths on that host)
 *   - Patterns WITH a slash are matched as a hostname/path PREFIX
 *     (e.g.  "api.foxnews.com/v3/alerts"  only blocks that path subtree)
 *   - * and ? wildcards work across the whole pattern including slashes
 *
 * Pass the result of hostnameFromUrl() as the first argument.
 */
export function isExcluded(hostAndPath, list) {
  if (!hostAndPath || !list || list.length === 0) return false;
  const subject = hostAndPath.toLowerCase();
  return list.some(pattern => {
    const p = pattern.toLowerCase();
    if (!p.includes('/')) {
      // hostname-only pattern — match just the host portion
      const host = subject.split('/')[0];
      return patternToRegex(p).test(host);
    }
    // path pattern — match as a prefix of host+path
    // Convert to regex that anchors at start but allows anything after the pattern
    const escaped = p.replace(/[.+^${}()|[\]\\]/g, '\\$&');
    const regexStr = escaped.replace(/\*/g, '.*').replace(/\?/g, '.');
    return new RegExp('^' + regexStr + '(\\/.*)?$', 'i').test(subject);
  });
}
